import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export function AdminIdeasPage() {
  const qc = useQueryClient();
  const [formatFilter, setFormatFilter] = useState<number | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'ideas'],
    queryFn: () => api.adminListIdeas(),
  });

  const formatsQuery = useQuery({
    queryKey: ['admin', 'formats'],
    queryFn: () => api.adminListFormats(),
    staleTime: 5 * 60_000,
  });
  const formats = formatsQuery.data?.formats ?? [];
  const formatName = (id: number) => formats.find((f) => f.id === id)?.name ?? `формат #${id}`;
  const activeFormats = formats.filter((f) => f.is_active);

  const del = useMutation({
    mutationFn: (id: number) => api.adminDeleteIdea(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ideas'] }),
  });

  function handleDelete(id: number, title: string) {
    if (!window.confirm(`Удалить идею «${title}»?`)) return;
    del.mutate(id);
  }

  const visible = (data?.ideas ?? []).filter(
    (idea) => formatFilter === null || idea.format_id === formatFilter,
  );

  const chip = (active: boolean) =>
    'px-3 py-1 rounded-full text-xs border ' +
    (active
      ? 'bg-tg-button text-tg-button-text border-transparent'
      : 'bg-tg-section-bg border-tg-hint opacity-70 hover:opacity-100');

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto">
      <header className="flex items-center justify-between mb-4">
        <Link to="/admin" className="text-sm opacity-70">
          ← Админка
        </Link>
        <h1 className="text-xl font-bold">Идеи</h1>
        <Link
          to="/admin/ideas/new"
          className="px-3 py-1 rounded-xl bg-tg-button text-tg-button-text text-sm font-medium"
        >
          + Новая
        </Link>
      </header>

      {activeFormats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button type="button" onClick={() => setFormatFilter(null)} className={chip(formatFilter === null)}>
            Все
          </button>
          {activeFormats.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFormatFilter(f.id)}
              className={chip(formatFilter === f.id)}
            >
              {f.name}
            </button>
          ))}
        </div>
      )}

      {isLoading && <p className="text-sm opacity-60 text-center py-8">Загрузка...</p>}

      {!isLoading && error && (
        <div className="text-center py-8">
          <p className="text-sm opacity-70 mb-3">Не удалось загрузить</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 rounded-xl bg-tg-button text-tg-button-text text-sm"
          >
            Повторить
          </button>
        </div>
      )}

      {data && data.ideas.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm opacity-60 mb-4">Идей пока нет</p>
          <Link
            to="/admin/ideas/new"
            className="px-4 py-2 rounded-xl bg-tg-button text-tg-button-text text-sm font-medium"
          >
            + Новая идея
          </Link>
        </div>
      )}

      {data && data.ideas.length > 0 && visible.length === 0 && (
        <p className="text-sm opacity-60 text-center py-8">В этом формате идей нет</p>
      )}

      {visible.length > 0 && (
        <div className="space-y-2">
          {visible.map((idea) => (
            <div key={idea.id} className="flex items-center gap-3 p-3 rounded-xl bg-tg-section-bg">
              {idea.thumbnail_url ? (
                <img
                  src={idea.thumbnail_url}
                  alt={idea.title}
                  width={50}
                  height={50}
                  className="rounded object-cover flex-shrink-0"
                  style={{ width: 50, height: 50 }}
                />
              ) : (
                <div
                  className="rounded bg-gray-300 flex-shrink-0"
                  style={{ width: 50, height: 50 }}
                />
              )}

              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{idea.title}</p>
                <p className="text-xs opacity-60 mt-0.5">
                  {formatName(idea.format_id)} · {idea.matryoshka} ·{' '}
                  {idea.is_active ? 'активна' : 'скрыта'}
                </p>
              </div>

              <div className="flex flex-col gap-1 flex-shrink-0">
                <Link
                  to={`/admin/ideas/${idea.id}/edit`}
                  className="px-2 py-1 rounded text-xs bg-tg-section-bg border border-tg-hint opacity-70 text-center hover:opacity-100"
                >
                  ✎ Редактировать
                </Link>
                <button
                  type="button"
                  onClick={() => handleDelete(idea.id, idea.title)}
                  disabled={del.isPending}
                  className="px-2 py-1 rounded text-xs bg-tg-section-bg border border-tg-hint opacity-70 text-left hover:opacity-100 disabled:opacity-40"
                >
                  ✕ Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
